<template>
  <b-row class="Reports mb-2">
    <b-col cols="12">
      <Notification v-bind.sync="notification" />

      <b-card>
        <b-card-title title-tag="h2" class="text-center mb-5">
          REPORTS
        </b-card-title>

        <b-nav tabs class="mb-3">
          <template v-for="(tab, idx) in tabList">
            <b-nav-item
              :key="`tab_${idx}`"
              :to="tab.link"
              :active="tab.active"
              :disabled="tab.disabled || isLoadingRequest"
            >
              {{ tab.label }}
            </b-nav-item>
          </template>
        </b-nav>

        <b-form inline class="row">
          <b-col cols="12" md="8" v-if="isTimesheet">
            <span class="font-weight-bold mb-0 col-sm-6 col-lg-2">
              Month:
            </span>
            <b-form-select
              class="rounded-0 border-primary"
              size="sm"
              v-model="selectMonth"
              :options="monthList"
            />
            <span class="font-weight-bold mb-0 col-sm-6 col-lg-2">
              Year:
            </span>
            <b-form-input
              class="rounded-0 border-primary w-25"
              size="sm"
              v-model="selectYear"
            />
            <span class="font-weight-bold mb-0 col-sm-6 col-lg-2">
              Cut-off:
            </span>
            <b-form-select
              class="rounded-0 border-primary"
              size="sm"
              v-model="selectCutoff"
              :options="cutoffList"
            />
          </b-col>
          <b-col cols="12" md="5" v-else>
            <b-input-group class="mb-1">
              <flat-pickr
                ref="dateRangePicker"
                id="date_range"
                v-model="dateRange"
                class="border-dark rounded-0 form-control-sm"
                placeholder="Report Date Range"
                :config="{
                  dateFormat: 'Y-m-d',
                  altFormat: 'F j Y',
                  altInput: true,
                  mode: 'range',
                }"
              />
              <b-input-group-append title="Clear Date Range">
                <b-button
                  squared
                  size="sm"
                  variant="outline-danger"
                  :disabled="!hasDateRange"
                  @click="cleardateRangePicker"
                >
                  <v-icon name="calendar-times" />
                </b-button>
              </b-input-group-append>
            </b-input-group>
            <b-form-text class="mb-1">Double click on the date to select a single date</b-form-text>
          </b-col>

          <b-col cols="12" md="4" style="align-self: baseline;">
            <b-button
              squared
              size="sm"
              variant="primary"
              :disabled="!hasDateRange && !isTimesheet"
              @click="() => generateReport(false)"
            >
              <v-icon name="table" />
              GENERATE REPORT
            </b-button>
            <b-button
              squared
              size="sm"
              variant="excel-green"
              class="ml-1"
              v-if="showCSVBtn"
              @click="exportCSV"
              :disabled="disableCSV"
            >
              <v-icon name="file-csv" />
              Export as CSV
            </b-button>
          </b-col>
        </b-form>

        <!-- TODO: filter and search -->
        <Datatable
          :resources="resources"
          :options="datatableOptions"
          @row-clicked="onRowClick"
          @update:perPage="updatePerPage"
          @updateSort="updateSort"
          @updatePage="updatePage"
        >
          <template v-slot:thead-top>
            <tr>
              <th colspan="2"><span class="sr-only">NUM and ID NUMBER</span></th>
              <th colspan="2" class="text-center">NAME</th>
              <th :colspan="isTardiness ? 3 : 7">
                <span class="sr-only">POSITION, DEPT, STATUS, DATE, TIME IN</span>
              </th>
              <th class="text-center" v-if="isTardiness">TARDINESS</th>
            </tr>
          </template>

          <template v-slot:mid-content>
            <b-badge v-if="total && !isLoadingRequest" class="mb-2">{{ total }} rows</b-badge>
            <Notification v-bind.sync="infoOptions">
              <h4>
                <v-icon name="mouse-pointer" scale="1.5" />
                Click on the report rows generated
              </h4>
              <span>
                In order to see additional details. (e.g., status, type, submit date, ...etc.)
              </span>
            </Notification>
          </template>

          <template v-slot:row-details="row">
            <b-card>
              <b-row class="mb-2">
                <b-col sm="3" class="text-sm-right"><b>POSITION:</b></b-col>
                <b-col>{{ $get(row, 'item.position', '') }}</b-col>

                <template v-if="reportType === 'tardiness'">
                  <b-col sm="3" class="text-sm-right">
                    <b>SHIFT SCHEDULE:</b>
                  </b-col>
                  <b-col>
                    <strong>({{ $get(row, 'item.shiftSchedule.ssName', '') }}) </strong>
                    <span>{{ $get(row, 'item.shiftSchedule.str', '') }}</span>
                  </b-col>
                </template>

                <template
                  v-else-if="['overtime', 'ob', 'leaves', 'timesheet'].includes(reportType)"
                >
                  <b-col sm="3" class="text-sm-right">
                    <b>STATUS:</b>
                  </b-col>
                  <b-col v-if="$get(row, 'item.status', null)">
                    <b-badge :variant="$get(row, 'item.statusVariant', 'primary')">
                      {{ $get(row, 'item.statusFormatted', '') }}
                    </b-badge>
                  </b-col>
                  <b-col v-else>-</b-col>
                </template>
              </b-row>

              <b-row class="mb-2">
                <b-col sm="3" class="text-sm-right"><b>EMPLOYMENT STATUS:</b></b-col>
                <b-col>{{ $title($get(row, 'item.employment_status', '')) }}</b-col>

                <template v-if="['overtime', 'ob'].includes(reportType)">
                  <b-col sm="3" class="text-sm-right"><b>SUBMIT DATE:</b></b-col>
                  <b-col>{{ $fd($get(row, 'item.created_at', ''), 'MMM DD YYYY HH:mm') }}</b-col>
                </template>
                <template v-else-if="reportType === 'leaves'">
                  <b-col sm="3" class="text-sm-right"><b>SUBMIT DATE:</b></b-col>
                  <b-col>{{ $fd($get(row, 'item.create_date', ''), 'MMM DD YYYY HH:mm') }}</b-col>
                </template>
              </b-row>

              <b-row v-if="reportType === 'leaves'" class="mb-2">
                <b-col sm="3" class="text-sm-right"><b>NO. OF DAYS:</b></b-col>
                <b-col sm="3">{{ $get(row, 'item.number_of_days', 0) }}</b-col>
              </b-row>
              <b-row
                class="mb-2"
                v-if="reportType === 'overtime'"
              >
                <b-col sm="3" class="text-sm-right"><b>TYPE:</b></b-col>
                <b-col sm="3">{{ $get(row, 'item.type', '') }}</b-col>

                <b-col sm="3" class="text-sm-right"><b>NIGHT DIFF:</b></b-col>
                <b-col sm="3">{{ Number($get(row, 'item.night_diff', 0) || 0).toFixed(2) }}</b-col>
              </b-row>
            </b-card>
          </template>
        </Datatable>
      </b-card>
    </b-col>
  </b-row>
</template>
<script>
import { mapGetters, mapActions, mapState } from 'vuex';
import Datatable from '@/components/Datatable';
import Notification from '@/components/Notification';
import ResourceService from '@/services/ResourceService';

const resource = new ResourceService();

export default {
  name: 'Reports',
  components: {
    Datatable,
    Notification,
  },
  data() {
    return {
      showInfo: false,
      showCSVBtn: false,
      dateRange: null,
      reportData: [],
      perPage: 10,
      page: 1,
      sortBy: 'entry_date',
      sortDesc: false,
      total: 0,
      currentMonth: null,
      selectMonth: new Date().getMonth() + 1,
      selectYear: new Date().getFullYear(),
      selectCutoff: new Date().getDate() <= 15 ? 0 : 1,
      monthList: [
        { value: 1, text: 'January' },
        { value: 2, text: 'February' },
        { value: 3, text: 'March' },
        { value: 4, text: 'April' },
        { value: 5, text: 'May' },
        { value: 6, text: 'June' },
        { value: 7, text: 'July' },
        { value: 8, text: 'August' },
        { value: 9, text: 'September' },
        { value: 10, text: 'October' },
        { value: 11, text: 'November' },
        { value: 12, text: 'December' },
      ],
    };
  },
  watch: {
    $route() {
      this.cleardateRangePicker();
      this.reportData = [];
      this.total = 0;
      this.perPage = 10;
      this.page = 1;
      this.showCSVBtn = false;
      this.showInfo = false;
    },
    dateRange() {
      if (this.hasData) {
        this.showCSVBtn = false;
      }
    },
    selectMonth() {
      if (this.hasData) {
        this.showCSVBtn = false;
      }
    },
    selectYear() {
      if (this.hasData) {
        this.showCSVBtn = false;
      }
    },
    selectCutoff() {
      if (this.hasData) {
        this.showCSVBtn = false;
      }
    },
  },
  computed: {
    resources() {
      return (this.reportData || []).map((row, idx) => {
        const otherData = {};
        switch (this.reportType) {
          case 'overtime':
          case 'ob': {
            const status = this.$get(row, 'status', '');
            const statusID = this.$get(row, 'status_id', null);
            const statusRef = {
              1: 'warning',
              2: 'success',
              3: 'danger',
            };
            otherData.statusFormatted = this.$upper(status);
            otherData.statusVariant = statusRef[statusID] || 'primary';
            break;
          }
          case 'leaves': {
            const status = this.$get(row, 'status', '');
            const statusRef = {
              for_approval: 'warning',
              approved: 'success',
              rejected: 'danger',
            };
            otherData.statusFormatted = this.$upper(status);
            otherData.statusVariant = statusRef[this.$snake(status)] || 'primary';
            break;
          }
          case 'timesheet': {
            const status = this.$get(row, 'status', '');
            const statusRef = {
              for_approval: 'warning',
              approved: 'success',
              rejected: 'danger',
            };
            otherData.statusFormatted = this.$upper(status);
            otherData.statusVariant = statusRef[this.$snake(status)] || 'primary';
            break;
          }
          default: break;
        }
        return Object.assign({}, row, { idx }, otherData);
      });
    },
    hasData() {
      return this.resources.length > 0;
    },
    reportType() {
      return this.$get(this.$route, 'meta.type', null);
    },
    isTardiness() {
      return this.reportType === 'tardiness';
    },
    isOT() {
      return this.reportType === 'overtime';
    },
    isLeaves() {
      return this.reportType === 'leaves';
    },
    isTimesheet() {
      return this.reportType === 'timesheet';
    },
    disableCSV() {
      return this.isLoadingRequest || this.resources.length === 0;
    },
    dateRangeArr() {
      if (!this.dateRange || typeof this.dateRange !== 'string') {
        return [];
      }
      return this.dateRange.split(' to ').filter(d => d);
    },
    tabList() {
      return [
        {
          link: { name: 'reports.tardiness' },
          label: 'Tardiness',
          active: this.navIsActive('reports.tardiness'),
        },
        {
          link: { name: 'reports.ot' },
          label: 'Overtime',
          active: this.navIsActive('reports.ot'),
        },
        {
          link: { name: 'reports.leaves' },
          label: 'Leaves Summary',
          active: this.navIsActive('reports.leaves'),
        },
        {
          link: { name: 'reports.timesheet' },
          label: 'Timesheet Summary',
          active: this.navIsActive('reports.timesheet'),
        },
      ];
    },
    datatableOptions() {
      return {
        small: true,
        pagination: true,
        dtControl: true,
        fields: this.tableFields || [],
        loading: this.isLoadingRequest,
        trClass: this.rowClass,
        total: this.total,
      };
    },
    tableFields() {
      const isLeaves = this.reportType === 'leaves';
      const numField = {
        label: '#',
        key: isLeaves ? 'leave_id' : 'id',
        class: 'text-center',
      };
      const commonFields = [
        numField,
        {
          key: 'employee_no',
          label: 'ID NUMBER',
          class: 'text-center',
        },
        {
          label: 'LAST NAME',
          key: 'last_name',
          class: 'text-center',
        },
        {
          label: 'FIRST NAME',
          key: 'first_name',
          class: 'text-center',
        },
        // {
        //   label: 'POSITION',
        //   key: 'position',
        //   sortable: true,
        //   class: 'text-center',
        // },
        {
          label: 'DEPARTMENT',
          key: 'department',
          class: 'text-center',
        },
        // {
        //   label: 'EMPLOYMENT STATUS',
        //   key: 'employment_status',
        //   sortable: true,
        //   class: 'text-center',
        // },
      ];
      switch (this.reportType) {
        case 'tardiness':
          return [
            ...commonFields,
            {
              label: 'DATE',
              key: 'entry_date',
              sortable: true,
              class: 'text-center',
              formatter: val => this.$fdr(val),
            },
            {
              label: 'TIME IN',
              key: 'time_in',
              sortable: true,
              class: 'text-center',
              formatter: val => this.$fto(val),
            },
            {
              label: 'MINUTES',
              key: 'lateMins',
              sortable: true,
              class: 'text-center',
            },
          ];
        case 'overtime':
          return [
            ...commonFields,
            {
              label: 'DATE',
              key: 'record_date',
              sortable: true,
              class: 'text-center',
              formatter: val => this.$fdr(val),
            },
            {
              label: 'START TIME',
              key: 'start_time',
              sortable: true,
              class: 'text-center',
              formatter: val => this.$fto(val),
            },
            {
              label: 'END TIME',
              key: 'end_time',
              sortable: true,
              class: 'text-center',
              formatter: val => this.$fto(val),
            },
            {
              label: 'TOTAL HOURS',
              key: 'total_hours',
              sortable: true,
              class: 'text-center',
              formatter: val => (Number(val) || 0).toFixed(2),
            },
          ];
        case 'leaves':
          return [
            ...commonFields,
            {
              label: 'START DATE',
              key: 'start_date',
              sortable: true,
              class: 'text-center',
              formatter: val => this.$fdr(val),
            },
            {
              label: 'END DATE',
              key: 'end_date',
              sortable: true,
              class: 'text-center',
              formatter: val => this.$fdr(val),
            },
            {
              label: 'LEAVE TYPE',
              key: 'leave_type',
              sortable: true,
              class: 'text-center',
            },
          ];
        case 'timesheet':
          return [
            ...commonFields,
            {
              label: 'STATUS',
              key: 'status',
              sortable: true,
              class: 'text-center',
            },
            {
              label: 'DATE SUBMITTED',
              key: 'formatted_date',
              sortable: true,
              class: 'text-center',
            },
          ];
        default:
          return null;
      }
    },
    hasDateRange() {
      return this.$get(this.dateRangeArr, 'length', 0) > 0;
    },
    infoOptions() {
      return {
        show: this.showInfo,
        dismissible: true,
        fade: true,
        variant: 'info',
      };
    },
    yearList() {
      const year = new Date().getFullYear();
      return Array.from({ length: year - 2019 }, (value, index) => 2020 + index);
    },
    cutoffList() {
      return [
        { value: 0, text: '1st to 15th' },
        { value: 1, text: '16th to end of the month' },
      ];
    },
    ...mapGetters(['isLoadingRequest']),
    ...mapState(['notification']),
  },
  methods: {
    updateSort(payload) {
      this.sortBy = this.$get(payload, 'sortBy', null);
      this.sortDesc = this.$get(payload, 'sortDesc', false);
      this.generateReport();
    },
    updatePerPage(val) {
      this.perPage = val;
      this.page = 1;
      this.generateReport();
    },
    updatePage(val) {
      this.page = val;
      this.generateReport();
    },
    cleardateRangePicker() {
      // eslint-disable-next-line no-underscore-dangle
      const dp = document.querySelector('#date_range')._flatpickr;
      dp.setDate(null);
      this.dateRange = null;
    },
    generateReport() {
      this.getReportData()
        .then(({
          success,
          data,
          message,
          total,
        }) => {
          if (success) {
            this.reportData = data;
            this.total = total || 0;

            if (this.reportData.length > 0) {
              // show info notif (about clicking details)
              this.showInfo = true;
              setTimeout(() => {
                this.showInfo = false;
              }, 5000);
            }

            this.showCSVBtn = this.reportData.length > 0;
          } else {
            this.setNotification({
              message,
              show: true,
              dismissible: true,
              icon: { name: 'exclamation' },
            });
          }
        });
    },
    getReportData(asCSV = false) {
      if (this.hasDateRange || this.isTimesheet) {
        this.startLoadingRequest();
        let dates = [];
        if (this.dateRangeArr.length === 1) {
          dates = [this.dateRangeArr[0], this.dateRangeArr[0]];
        } else {
          dates = this.dateRangeArr;
        }
        let params = {
          asCSV,
          date: dates,
          page: this.page,
          perPage: this.perPage,
          sortBy: this.sortBy,
          sortDesc: this.sortDesc,
        };
        if (this.isTimesheet) {
          params = Object.assign({}, params, {
            month: this.selectMonth,
            year: this.selectYear,
            cutoff: this.selectCutoff,
          });
        }
        return resource.post(`/report/${this.reportType}`, params)
          .then(result => result)
          .catch((e) => {
            this.setNotification({
              show: true,
              message: 'An error has occurred.',
              dismissible: true,
              icon: { name: 'exclamation' },
            });
            throw e;
          })
          .then((result) => {
            this.stopLoadingRequest();
            return result;
          });
      }
      return new Promise(null);
    },
    onRowClick(payload) {
      const idx = this.$get(payload, 'idx', null);
      if (idx !== null) {
        /* eslint-disable no-underscore-dangle */
        const newState = !this.reportData[idx]._showDetails;
        // show row details (only 1 at a time)
        this.reportData = this.reportData.map((row, rowIdx) => {
          if (idx === rowIdx) {
            return Object.assign({}, row, { _showDetails: newState });
          }
          return Object.assign({}, row, { _showDetails: false });
        });
        /* eslint-enable */
      }
    },
    rowClass(item, type) {
      if (type === 'row') {
        return 'clickable';
      }
      return '';
    },
    exportCSV() {
      this.getReportData(true)
        .then(({ success, data, message }) => {
          if (success) {
            this.$fcsv(data, `${this.reportType}-${Date.now()}.csv`);
          } else {
            this.setNotification({
              show: true,
              message: message || 'An error has occurred.',
              dismissible: true,
              icon: { name: 'exclamation' },
            });
          }
        })
        .catch(() => this.setNotification({
          show: true,
          message: 'An error has occurred.',
          dismissible: true,
          icon: { name: 'exclamation' },
        }));
    },
    ...mapActions([
      'setNotification',
      'startLoadingRequest',
      'stopLoadingRequest',
      'startLoadingPage',
      'stopLoadingPage',
    ]),
  },
};
</script>
